import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DotCarousel from '../components/DotCarousel';
import { HighlightCard, type HighlightItem } from '../components/HighlightCard';
import { PosterCard, type PosterItem } from '../components/PosterCard';
import {
  projectEssentialApi,
  type EdmTabItem,
  type GlsLandSaleItem,
  type LatestHighlightItem,
  type UpcomingProject,
} from '../services/api';
import ProjectResourcesDialog from '../components/ProjectResourcesDialog';
import { ProjectCoreTeamDialog } from '../components/ProjectCoreTeamDialog';
import ProjectNavButtons from '../components/ProjectNavButtons';
import PageSearchBar from '../components/PageSearchBar';
import ProjectFiltersDialog from '../components/ProjectFiltersDialog';
import { usePeEmbed } from '../embedContext';
import { useAppliedFilterChips } from '../hooks/useAppliedFilterChips';
import { usePeFilters } from '../hooks/usePeFilters';
import { usePeSearchDropdown } from '../hooks/usePeSearchDropdown';
import { GlsCard } from '../components/GlsCard';
import { buildPeSearchPageHref, countPeFilters, hasPeProjectFilter } from '../utils/searchState';
import {
  logEdmPreviewed,
  logEdmDownloaded,
  logProjectDetailsPageClicked,
  logProjectResourcesClicked,
} from '../services/analytics';
import {
  canOpenProjectMaterials,
  projectMaterialsLabel,
} from '../utils/materialsLabels';

const SECTION_GAP = { xs: 2.5, sm: 3, md: 3.5 };

// The top-of-page Pre Filters + Category buttons live in the shared
// `ProjectNavButtons` component (see the import above). Seminars &
// Events / Videos are reachable from there, so the dashboard no
// longer renders a duplicate footer row.

// Cap the Upcoming GLS rail at 10 items (newest DateOfAward first).
// The full list lives on the Upcoming Projects page carousel.
const MAX_UPCOMING_GLS = 10;

// Cap the Recent EDM rail at the same 10 items the EDM page uses for
// its "Uploaded Recently" carousel — keeps the two surfaces in sync.
const MAX_RECENT_EDMS = 10;

// Slide widths copied from `ProjectEdmPage`'s `EDM_SLIDE_FLEX` so the
// rail looks identical on both pages.
const EDM_SLIDE_FLEX = {
  xs: '0 0 52%',
  sm: '0 0 36%',
  md: '0 0 28%',
  lg: '0 0 22%',
};

// Pre-format an ISO `createdAt` into "08 May 2026" — same helper
// `ProjectEdmPage` uses, duplicated here to keep this page self-contained.
// Returns '' when the timestamp is missing so the card just hides the
// date row instead of printing "Invalid Date".
function formatEdmDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Adapter: backend `EdmTabItem` → shared `PosterItem` shape consumed
// by `PosterCard`. Mirrors the mapping in `ProjectEdmPage.mapTabItem`
// so both rails render with identical metadata semantics.
function mapEdmToPoster(item: EdmTabItem): PosterItem {
  return {
    id: item.id,
    title: item.title,
    date: formatEdmDate(item.createdAt),
    cover: item.cover,
    url: item.url,
    urlPublic: item.urlPublic,
    filename: item.filename,
    downloads: item.downloads,
  };
}

// Cap the Recent Highlights rail at the same 10 items the Highlights
// & eBook page uses for its "Latest Highlights" section — keeps the
// two surfaces in sync.
const MAX_RECENT_HIGHLIGHTS = 10;

// Same accent-blue palette `HighlightsAndEbookPage` uses for its
// generated covers — kept identical so a launcher's card looks the
// same on both surfaces.
const HIGHLIGHT_COVER_BGS = [
  '1f3a5f',
  '24487a',
  '2c5b8a',
  '3d6c97',
  '4f7da6',
  '5a85ac',
  '6692b8',
];

// Adapter: backend `LatestHighlightItem` → shared `HighlightItem`.
// Mirrors the `adaptHighlight` helper in `HighlightsAndEbookPage` so
// the two surfaces present each launcher identically (same card
// component, same cover palette, same date formatting).
function mapLatestHighlight(
  item: LatestHighlightItem,
  index: number,
): HighlightItem {
  const dateLabel = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '';
  const bg = HIGHLIGHT_COVER_BGS[index % HIGHLIGHT_COVER_BGS.length];
  return {
    id: item.id,
    title: item.title,
    date: dateLabel,
    duration: item.type || 'PDF',
    cover: item.cover ?? null,
    image: item.cover || `https://placehold.co/600x340/${bg}/ffffff/png?text=${encodeURIComponent(
      item.title,
    )}`,
    url: item.url,
    filename: `${item.title || item.id}.pdf`,
    downloads: item.downloads,
  };
}

function SectionHeader({
  title,
  viewAllHref,
}: {
  title: string;
  /**
   * When provided, renders a "View All" link on the right that navigates
   * to the given route. Rendered as a real `<a>` (via react-router's
   * `Link`) so the browser supplies the native pointer cursor and the
   * link supports middle-click / open-in-new-tab natively.
   */
  viewAllHref?: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontSize: { xs: '0.95rem', sm: '1.05rem' },
        }}
      >
        {title}
      </Typography>
      {viewAllHref && (
        // Using MUI's `Link` (which carries `cursor: pointer` in its
        // base styles) on top of react-router's `Link` so SPA navigation
        // still works while we get a real anchor element with the
        // browser's native pointer cursor. `inline-flex` + generous
        // padding ensures the hit area is one continuous box covering
        // glyphs *and* the surrounding line-height / letter-spacing
        // gutters, so the cursor never reverts as the mouse moves
        // around inside the link.
        <Link
          component={RouterLink}
          to={viewAllHref}
          underline="none"
          sx={{
            color: 'var(--color-accent)',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            px: 1,
            py: 0.75,
            mx: -1,
            my: -0.75,
            borderRadius: 0.75,
            userSelect: 'none',
            '&:hover': { color: 'var(--color-accent-strong)' },
            '&:focus-visible': {
              outline: '2px solid var(--color-accent)',
              outlineOffset: 2,
            },
          }}
        >
          View All
        </Link>
      )}
    </Box>
  );
}

const CARD_BASE_SX = {
  bgcolor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--color-shadow-card)',
  borderRadius: 2,
  overflow: 'hidden',
  scrollSnapAlign: 'start',
  display: 'flex',
  flexDirection: 'column',
} as const;

// ---------------------------------------------------------------------------
// FeaturedProjectCard — hero variant used only by the dashboard
// "Featured" carousel. Image fills the right-hand side; a dark
// gradient on the left holds the project name, the standard
// location / units / Est-TOP detail rows (mirroring `ProjectCard`)
// and a pair of "Project Details" + "Project Materials" CTAs.
// ---------------------------------------------------------------------------
function FeaturedProjectCard({ project }: { project: UpcomingProject }) {
  const navigate = useNavigate();
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [coreTeamOpen, setCoreTeamOpen] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = Boolean(project.coverImage) && !coverFailed;

  const handleDetails = () => {
    logProjectDetailsPageClicked(project.id);
    navigate(`/project-essential/projects/${encodeURIComponent(project.id)}`);
  };
  const handleResources = () => {
    if (!canOpenProjectMaterials(project.hasMaterials)) return;
    logProjectResourcesClicked(project.id);
    setResourcesOpen(true);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 280, sm: 380, md: 460, lg: 530 },
        borderRadius: 2,
        overflow: 'hidden',
        flex: '0 0 auto',
        scrollSnapAlign: 'start',
        // Solid fallback if the cover never loads — so the gradient
        // panel still has something dark behind it instead of bare
        // surface colour.
        bgcolor: '#0f1a32',
      }}
    >
      {/* Cover image fills the whole card; the gradient overlay
          above tints the left half so the text stays readable. */}
      {showCover && (
        <Box
          component="img"
          src={project.coverImage!}
          alt={project.name || project.id}
          loading="lazy"
          onError={() => setCoverFailed(true)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            // `fill` so the image is stretched to span the full card
            // (both width and height). The card enforces a fixed
            // height per breakpoint, and we want the artwork to
            // occupy every pixel rather than letterboxing.
            objectFit: 'fill',
            display: 'block',
          }}
        />
      )}
      {/* Bottom-anchored dark gradient — keeps the lower half of
          the photo dark enough for white text to read against it
          while leaving the top half of the hero image untouched.
          A slight left bias makes sure the bottom-left corner
          (where the content lives) is fully covered even when the
          card is very wide. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(15,26,50,0) 30%, rgba(15,26,50,0.55) 60%, rgba(15,26,50,0.92) 100%), ' +
            'linear-gradient(90deg, rgba(15,26,50,0.55) 0%, rgba(15,26,50,0) 55%)',
        }}
      />

      {/* Foreground content — anchored to the bottom-left corner of
          the card so the photo above can breathe. `maxWidth` caps
          the text column on wide cards so titles don't bleed across
          the whole hero. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          color: '#fff',
          px: { xs: 1.75, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 4.5, sm: 6 },
          maxWidth: { xs: '100%', sm: '75%', md: '65%', lg: '55%' },
          gap: { xs: 0.9, sm: 1.5 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.15,
            fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.8rem' },
          }}
        >
          {project.name || project.id}
        </Typography>

        {/* Detail rows — same icon vocabulary as `ProjectCard` so
            the two surfaces feel like one product family, just
            recoloured for the dark hero. */}
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.85)' }} />
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.92)' }}
              noWrap
            >
              {[
                project.region,
                project.district && `D${project.district.replace(/^D+/i, '')}`,
              ]
                .filter(Boolean)
                .join(' • ') ||
                project.area ||
                '—'}
            </Typography>
          </Stack>
          {(project.totalUnits || project.topDate != null) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              divider={
                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: 'rgba(255,255,255,0.4)',
                  }}
                />
              }
            >
              {project.totalUnits && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <GroupsIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.85)' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.92)' }}
                    noWrap
                  >
                    {/^\d+$/.test(project.totalUnits)
                      ? `${project.totalUnits} Units`
                      : project.totalUnits}
                  </Typography>
                </Stack>
              )}
              {project.topDate != null && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarMonthIcon
                    sx={{ fontSize: 16, color: 'rgba(255,255,255,0.85)' }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.92)' }}
                    noWrap
                  >
                    Est. {project.topDate}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>

        {/* CTAs — Project Details becomes a full-width footer button, while Project Materials and Core Team sit above it side-by-side. */}
        <Stack spacing={{ xs: 0.75, sm: 1 }} sx={{ mt: { xs: 0.25, sm: 1 }, width: '100%' }}>
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1.25 }}>
            <Button
              onClick={handleResources}
              variant="outlined"
              fullWidth
              disabled={!canOpenProjectMaterials(project.hasMaterials)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                py: { xs: 0.4, sm: 0.9 },
                borderRadius: 1,
                borderColor: 'rgba(255,255,255,0.85)',
                color: '#fff',
                bgcolor: 'transparent',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255,255,255,0.45)',
                  color: 'rgba(255,255,255,0.65)',
                },
              }}
            >
              {projectMaterialsLabel(project.hasMaterials)}
            </Button>
            <Button
              onClick={() => setCoreTeamOpen(true)}
              variant="outlined"
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                py: { xs: 0.4, sm: 0.9 },
                borderRadius: 1,
                borderColor: 'rgba(255,255,255,0.85)',
                color: '#fff',
                bgcolor: 'transparent',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              Project Taggers
            </Button>
          </Stack>
          <Button
            onClick={handleDetails}
            variant="contained"
            fullWidth
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              py: { xs: 0.45, sm: 0.9 },
              borderRadius: 1,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': { bgcolor: 'var(--color-accent-strong)' },
            }}
          >
            Project Details
          </Button>
        </Stack>
      </Box>

      <ProjectResourcesDialog
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
        projectId={project.id}
        projectName={project.name}
      />
      <ProjectCoreTeamDialog
        open={coreTeamOpen}
        onClose={() => setCoreTeamOpen(false)}
        projectId={project.id}
        projectName={project.name}
      />
    </Card>
  );
}

// Exported so the Upcoming Projects listing page can render the same
// card inside its own carousel without duplicating the layout. The
// dashboard's "Featured" hero is a different component
// (`FeaturedProjectCard` above) — this card only handles the dense
// carousel layout with a fixed width and a contained cover image.
export function ProjectCard({ project }: { project: UpcomingProject }) {
  const navigate = useNavigate();
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [coreTeamOpen, setCoreTeamOpen] = useState(false);
  // Tracks whether the cover image failed to load. When true we render
  // the same "No image" placeholder used for projects with no cover URL,
  // so the card height stays consistent instead of collapsing.
  const [coverFailed, setCoverFailed] = useState(false);
  // Landscape covers use `fill` to stretch into the frame; portrait stays
  // `contain` so tall renders aren't cropped. Defaults to contain until
  // natural dimensions are known.
  const [coverIsLandscape, setCoverIsLandscape] = useState(false);
  const showCover = Boolean(project.coverImage) && !coverFailed;

  // Reset orientation when the project / cover URL changes so a recycled
  // card doesn't keep the previous image's landscape flag.
  useEffect(() => {
    setCoverFailed(false);
    setCoverIsLandscape(false);
  }, [project.coverImage]);

  const handleDetails = () => {
    logProjectDetailsPageClicked(project.id);
    navigate(`/project-essential/projects/${encodeURIComponent(project.id)}`);
  };

  const handleResources = () => {
    if (!canOpenProjectMaterials(project.hasMaterials)) return;
    logProjectResourcesClicked(project.id);
    setResourcesOpen(true);
  };

  return (
    <Card
      sx={{
        ...CARD_BASE_SX,
        // Fill the carousel slide — fixed px widths overflow the
        // percentage-based slide flex on mobile/tablet and stack.
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        flex: '1 1 auto',
        flexShrink: 1,
        boxSizing: 'border-box',
      }}
    >
      {/* Cover area is a real <button> so clicking either the project
          image or the "No image" fallback navigates to the project
          detail page. `all: 'unset'` strips default browser button
          chrome so the visual is identical to the old static cover —
          only the cursor and a subtle hover/focus hint change.
          Keyboard focus + Enter/Space activation come for free from
          `component="button"`. */}
      <Box
        component="button"
        type="button"
        onClick={handleDetails}
        aria-label={`Open ${project.name || project.id} details`}
        sx={{
          all: 'unset',
          display: 'block',
          width: '100%',
          cursor: 'pointer',
          '&:hover': { opacity: 0.92 },
          '&:focus-visible': {
            outline: '2px solid var(--color-accent)',
            outlineOffset: -2,
          },
        }}
      >
        {showCover ? (
          <CardMedia
            component="img"
            image={project.coverImage!}
            alt={project.name || project.id}
            // Landscape → `fill` stretches to the frame (no letterboxing,
            // no crop). Portrait / square → `contain` so the full
            // building shows without distortion.
            sx={{
              height: { xs: 170, sm: 200, md: 230, lg: 250 },
              objectFit: coverIsLandscape ? 'fill' : 'contain',
              objectPosition: 'center',
              bgcolor: 'var(--color-surface-elevated)',
            }}
            onLoad={(e) => {
              const img = e.currentTarget;
              setCoverIsLandscape(img.naturalWidth > img.naturalHeight);
            }}
            onError={() => setCoverFailed(true)}
          />
        ) : (
          <Box
            sx={{
              height: { xs: 170, sm: 200, md: 230, lg: 250 },
              bgcolor: 'var(--color-surface-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-muted)',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            No image
          </Box>
        )}
      </Box>
      {/* ── Identity group: title + developer (tight proximity) ── */}
      <CardContent
        sx={{
          p: 1.5,
          pb: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: 0 },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.25 }}
          noWrap
        >
          {project.name || project.id}
        </Typography>
        {/* Developer subtitle is intentionally omitted on the dense
            carousel cards — the units / Est-TOP rows below carry
            the marketing weight and the title's already noWrap'd to
            keep the card tight. */}

        {/* ── Context rows ──
            Stacked icon+text rows beneath the title: location, unit
            count, and an estimated launch month. Each row is hidden
            individually when its data is missing so the card never
            shows a blank icon next to nothing. */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
          <LocationOnIcon sx={{ fontSize: 14, color: 'var(--color-text-muted)' }} />
          <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }} noWrap>
            {/* Strip any leading "D"s before re-prefixing — the source
                column is inconsistent ("18" vs "D18" vs "d18"), and a
                naive `D${project.district}` produces "DD18" for rows
                already prefixed. */}
            {[
              project.region,
              project.district && `D${project.district.replace(/^D+/i, '')}`,
            ]
              .filter(Boolean)
              .join(' • ') ||
              project.area ||
              '—'}
          </Typography>
        </Stack>
        {/* Units + Est launch share a single row with a vertical
            divider between them — matches the dashboard reference.
            Each half is hidden individually when its data is
            missing; if both are missing the whole row collapses. */}
        {(project.totalUnits || project.topDate != null) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.75 }}
            divider={
              <Box
                sx={{
                  width: '1px',
                  alignSelf: 'stretch',
                  bgcolor: 'var(--color-border)',
                }}
              />
            }
          >
            {project.totalUnits && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <GroupsIcon sx={{ fontSize: 14, color: 'var(--color-text-muted)' }} />
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--color-text-muted)' }}
                  noWrap
                >
                  {/* Append "Units" only when the source value is
                      purely numeric — the curated label sometimes
                      already includes the suffix (e.g. "200 Units"). */}
                  {/^\d+$/.test(project.totalUnits)
                    ? `${project.totalUnits} Units`
                    : project.totalUnits}
                </Typography>
              </Stack>
            )}
            {project.topDate != null && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarMonthIcon sx={{ fontSize: 14, color: 'var(--color-text-muted)' }} />
                <Typography
                  variant="caption"
                  sx={{ color: 'var(--color-text-muted)' }}
                  noWrap
                >
                  {/* `topDate` is the project's expected TOP year as
                      an integer (e.g. 2026), not a calendar date. */}
                  Est. {project.topDate}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>

      {/* ── Actions group ──
          Stacked primary + secondary CTAs anchored to the
          bottom of the card, matching the dashboard reference. */}
      {(() => {
        const isGls = Boolean(project.name?.toLowerCase().includes('gls'));
        if (isGls) {
          return (
            <Stack
              spacing={1}
              sx={{
                mt: 1.25,
                px: 1.5,
                pt: 1.25,
                pb: 1.25,
                borderTop: '1px solid var(--color-border)',
                bgcolor: 'var(--color-surface-elevated)',
              }}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={handleResources}
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={!canOpenProjectMaterials(project.hasMaterials)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.74rem',
                    lineHeight: 1.2,
                    py: 0.6,
                    borderRadius: 1,
                    borderColor: 'var(--color-accent)',
                    color: 'var(--color-accent-strong)',
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: 'var(--color-accent-strong)',
                      bgcolor: 'var(--color-accent-soft)',
                    },
                  }}
                >
                  {projectMaterialsLabel(project.hasMaterials)}
                </Button>
                <Button
                  onClick={handleDetails}
                  variant="contained"
                  fullWidth
                  size="small"
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    py: 0.6,
                    borderRadius: 1,
                    bgcolor: 'var(--color-accent)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                  }}
                >
                  Project Details
                </Button>
              </Stack>
            </Stack>
          );
        }

        return (
          <Stack
            spacing={1}
            sx={{
              mt: 1.25,
              px: 1.5,
              pt: 1,
              pb: 1.25,
              borderTop: '1px solid var(--color-border)',
              bgcolor: 'var(--color-surface-elevated)',
            }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleResources}
                variant="outlined"
                fullWidth
                size="small"
                disabled={!canOpenProjectMaterials(project.hasMaterials)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.74rem',
                  lineHeight: 1.2,
                  py: 0.6,
                  borderRadius: 1,
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent-strong)',
                  bgcolor: 'transparent',
                  '&:hover': {
                    borderColor: 'var(--color-accent-strong)',
                    bgcolor: 'var(--color-accent-soft)',
                  },
                }}
              >
                {projectMaterialsLabel(project.hasMaterials)}
              </Button>
              <Button
                onClick={() => setCoreTeamOpen(true)}
                variant="outlined"
                fullWidth
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.74rem',
                  lineHeight: 1.2,
                  py: 0.6,
                  borderRadius: 1,
                  borderColor: 'var(--color-accent)',
                  color: 'var(--color-accent-strong)',
                  bgcolor: 'transparent',
                  '&:hover': {
                    borderColor: 'var(--color-accent-strong)',
                    bgcolor: 'var(--color-accent-soft)',
                  },
                }}
              >
                Project Taggers
              </Button>
            </Stack>
            <Button
              onClick={handleDetails}
              variant="contained"
              fullWidth
              size="small"
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.8rem',
                py: 0.6,
                borderRadius: 1,
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                '&:hover': { bgcolor: 'var(--color-accent-strong)' },
              }}
            >
              Project Details
            </Button>
          </Stack>
        );
      })()}
      <ProjectResourcesDialog
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
        projectId={project.id}
        projectName={project.name}
      />
      <ProjectCoreTeamDialog
        open={coreTeamOpen}
        onClose={() => setCoreTeamOpen(false)}
        projectId={project.id}
        projectName={project.name}
      />
    </Card>
  );
}


export default function Dashboard() {
  const navigate = useNavigate();
  const isEmbed = usePeEmbed();
  // Curated rails are not narrowed by filters; filters apply to the
  // search dropdown and are carried into the results page on submit.
  const [filters, setFilters] = usePeFilters();
  const { search, setSearch, dismissDropdownKey, dropdownContent } =
    usePeSearchDropdown(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCount = countPeFilters(filters);
  const hasProjectFilter = hasPeProjectFilter(filters);
  const { chips: appliedFilterChips, clearAll: clearAllFilters } =
    useAppliedFilterChips(filters, setFilters);
  const [glsItems, setGlsItems] = useState<GlsLandSaleItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Featured projects state
  const [featuredProjects, setFeaturedProjects] = useState<UpcomingProject[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredError, setFeaturedError] = useState<string | null>(null);

  // Recent EDMs — fetched once via the same `/edms/tab/recent` endpoint
  // the EDM page uses, mapped through `mapEdmToPoster` so the rail
  // renders identical `PosterCard`s.
  const [edms, setEdms] = useState<PosterItem[]>([]);
  const [edmsLoading, setEdmsLoading] = useState(true);
  const [edmsError, setEdmsError] = useState<string | null>(null);
  // Recent Highlights — same `/highlights/latest` endpoint that drives
  // `HighlightsAndEbookPage`'s "Latest Highlights" rail. Capped at the
  // same 10 items so the two surfaces present an identical short list.
  const [highlights, setHighlights] = useState<HighlightItem[]>([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState<string | null>(null);

  // Upcoming GLS carousel — ProjectLandSale rows where ProjectName IS NULL.
  // Mirrors the Upcoming Projects listing page.
  useEffect(() => {
    if (hasProjectFilter) {
      setGlsItems([]);
      setProjectsLoading(false);
      setProjectsError(null);
      return;
    }
    let mounted = true;
    setProjectsLoading(true);
    setProjectsError(null);
    projectEssentialApi
      .getGlsLandSales()
      .then((res) => {
        if (mounted) setGlsItems(res.items.slice(0, MAX_UPCOMING_GLS));
      })
      .catch((err) => {
        if (mounted) setProjectsError(err?.message || 'Failed to load upcoming GLS');
      })
      .finally(() => {
        if (mounted) setProjectsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [hasProjectFilter]);

  useEffect(() => {
    let mounted = true;
    projectEssentialApi
      .getEdmsByTab('recent', MAX_RECENT_EDMS)
      .then((res) => {
        if (mounted) setEdms(res.items.map(mapEdmToPoster));
      })
      .catch((err) => {
        if (mounted) setEdmsError(err?.message || 'Failed to load EDMs');
      })
      .finally(() => {
        if (mounted) setEdmsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch the latest highlights from the same endpoint the Highlights
  // & eBook page uses, so the dashboard rail and the dedicated page
  // always stay in sync. Capped at `MAX_RECENT_HIGHLIGHTS` (10).
  useEffect(() => {
    let mounted = true;
    setHighlightsLoading(true);
    setHighlightsError(null);
    projectEssentialApi
      .getLatestHighlights(MAX_RECENT_HIGHLIGHTS)
      .then((res) => {
        if (mounted) {
          const sorted = [...res.items].sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
          });
          setHighlights(sorted.map(mapLatestHighlight));
        }
      })
      .catch((err) => {
        if (mounted) {
          setHighlightsError(
            (err as Error)?.message || 'Failed to load latest highlights',
          );
        }
      })
      .finally(() => {
        if (mounted) setHighlightsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch featured projects from the backend endpoint
  useEffect(() => {
    let mounted = true;
    setFeaturedLoading(true);
    setFeaturedError(null);
    projectEssentialApi
      .getFeaturedProjects()
      .then((res) => {
        if (mounted) setFeaturedProjects(res.items);
      })
      .catch((err) => {
        if (mounted) {
          setFeaturedError(
            (err as Error)?.message || 'Failed to load featured projects',
          );
        }
      })
      .finally(() => {
        if (mounted) setFeaturedLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Title for the GLS section. Carousel is sourced from ProjectLandSale
  // (ProjectName IS NULL), matching the Upcoming Projects listing page.
  const listTitle = 'Upcoming GLS / Enbloc';
  const emptyMessage = 'No upcoming GLS / Enbloc right now.';

  return (
    <Stack spacing={isEmbed ? { xs: 1.5, sm: 2 } : SECTION_GAP}>
      {/* Page intro — hidden in iframe embed; navbar chrome is omitted there. */}
      {!isEmbed && (
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: 'var(--color-text-strong)',
              lineHeight: 1.15,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
            }}
          >
            Project Essentials
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', mt: 0.5, lineHeight: 1.4 }}
          >
            Curated project insights, marketing collaterals and client-ready tools — all in one place.
          </Typography>
        </Box>
      )}

      <PageSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search projects, EDMs, eBooks and reports…"
        onSubmit={(query) => navigate(buildPeSearchPageHref(query, filters))}
        filterCount={filterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        appliedFilters={appliedFilterChips}
        onClearAllFilters={clearAllFilters}
        dropdownContent={dropdownContent}
        dismissDropdownKey={dismissDropdownKey}
      />

      {/* Shared Pre Filters + Category buttons row. Same component
          (and therefore identical spacing) as the other Project
          Essential pages — keeps the navigation grammar consistent
          across the entire app. */}
      <ProjectNavButtons />

      <ProjectFiltersDialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onApply={(next) => {
          navigate(buildPeSearchPageHref(search.trim(), next));
        }}
      />

      {/* Section 1: Featured — full-width single-slide carousel that
          leads the dashboard. Uses the dedicated `FeaturedProjectCard`
          (hero layout: image right, dark gradient + content left)
          rather than the dense `ProjectCard` so the visual hierarchy
          matches the marketing reference. Wired to getFeaturedProjects(). */}
      <Box>
        <SectionHeader title="Featured Projects" />
        {featuredLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : featuredError ? (
          <Typography variant="body2" color="error">
            {featuredError}
          </Typography>
        ) : featuredProjects.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2 }}>
            No featured projects available.
          </Typography>
        ) : (
          <DotCarousel
            slides={featuredProjects.map((p) => ({
              key: p.id,
              node: <FeaturedProjectCard project={p} />,
            }))}
            // One card per viewport across all breakpoints — featured
            // items get the full stage rather than a peek-into-next.
            slideFlex={{ xs: '0 0 100%', sm: '0 0 100%', md: '0 0 100%', lg: '0 0 100%' }}
            gap={{ xs: 1, sm: 1.25 }}
            // Dots overlay the hero image (white-on-dark) instead of
            // sitting in a separate row below — matches the marketing
            // reference where pagination floats inside the card.
            dotsOverlay
          />
        )}
      </Box>

      {/* Section 2: Upcoming GLS — carousel hidden when filtering by project. */}
      <Box>
        <SectionHeader
          title={listTitle}
          viewAllHref="/project-essential/upcoming-projects"
        />
        {hasProjectFilter ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            {emptyMessage}
          </Typography>
        ) : projectsLoading && glsItems.length === 0 ? (
          // First-time load with no cached results yet — show the full spinner.
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : projectsError ? (
          <Typography variant="body2" color="error">
            {projectsError}
          </Typography>
        ) : glsItems.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            {emptyMessage}
          </Typography>
        ) : (
          // Subsequent refetches keep the previous results visible; the
          // SearchBar's inline spinner conveys the in-flight state, and the
          // scroller dims slightly to hint that the data is being refreshed.
          <Box sx={{ position: 'relative', transition: 'opacity 150ms ease', opacity: projectsLoading ? 0.55 : 1 }}>
            <DotCarousel
              slides={glsItems.map((item) => ({
                key: String(item.id),
                node: <GlsCard item={item} />,
              }))}
              // Slide widths roughly match the card widths defined in
              // `GlsCard` (xs 260 → lg 400) so each card sits in
              // its own slot without leaving large empty gutters on
              // wide screens.
              // Mobile slide is widened to 94% so the next card
              // peeks just enough to signal the carousel without
              // leaving a visible gutter between the active and
              // peeking cards. Desktop sizes unchanged.
              slideFlex={{ xs: '0 0 50%', sm: '0 0 40%', md: '0 0 32%', lg: '0 0 32%' }}
              gap={{ xs: 0.25, sm: 1.25 }}
            />
          </Box>
        )}
      </Box>

      {/* Section 3: Latest Highlights & eBook — capped at 10 most
          recent. Fed by the same `/highlights/latest` endpoint that
          drives the Highlights & eBook page's "Latest Highlights"
          rail, so both surfaces always agree on the short list. */}
      <Box>
        <SectionHeader
          title="Latest Highlights & eBook"
          viewAllHref="/project-essential/highlights-ebook"
        />
        {highlightsLoading && highlights.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : highlightsError && highlights.length === 0 ? (
          <Typography variant="body2" color="error">
            {highlightsError}
          </Typography>
        ) : highlights.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            No highlights available.
          </Typography>
        ) : (
          <DotCarousel
            slides={highlights.map((h) => ({
              key: h.id,
              node: (
                <HighlightCard
                  item={h}
                  shareKind="ebook"
                />
              ),
            }))}
            slideFlex={{ xs: '0 0 70%', sm: '0 0 42%', md: '0 0 32%', lg: '0 0 24%' }}
            gap={{ xs: 0.5, sm: 1.25 }}
          />
        )}
      </Box>

      {/* Section 4: Latest EDM */}
      <Box>
        <SectionHeader
          title="Latest EDMs"
          viewAllHref="/project-essential/project-edm"
        />
        {edmsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : edmsError ? (
          <Typography variant="body2" color="error">
            {edmsError}
          </Typography>
        ) : edms.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            No EDMs available.
          </Typography>
        ) : (
          // Same `DotCarousel` + `PosterCard` rail used by
          // `ProjectEdmPage`'s "Uploaded Recently" section, so the two
          // surfaces look and feel identical.
          <DotCarousel
            slides={edms.map((item) => ({
              key: item.id,
              node: (
                <PosterCard
                  item={item}
                  onPreview={(i) => logEdmPreviewed(i.id)}
                  onDownloadClick={(i) => logEdmDownloaded(i.id)}
                  shareKind="edm"
                />
              ),
            }))}
            slideFlex={EDM_SLIDE_FLEX}
            gap={{ xs: 0.5, sm: 1.25 }}
          />
        )}
      </Box>

    </Stack>
  );
}
